import type { JSX } from 'react';
import { IconUser } from '@tabler/icons-react';
import { Avatar } from '@mantine/core';
import { isDefaultGoogleAvatarUrl } from '../../utils';
import type { IUserAvatarProps } from './types';

/** A radius large enough to clamp to a full circle at any avatar size. */
const CIRCLE_RADIUS = 9999;

/**
 * A person's identity as an avatar, rendered the same way on every page. When a
 * real photo exists it shows the photo. With no photo but a name, it shows the
 * name initials on a color derived from the name (stable per person, so a roster
 * of photo-less people no longer reads as identical grey icons). With neither, it
 * falls back to the generic `IconUser`.
 *
 * Pins a full-circle radius so the shape is explicit and independent of theme
 * defaults; a caller can still override `radius`. Sets
 * `referrerPolicy: 'no-referrer'` because Google profile photos
 * (`lh3.googleusercontent.com`) return 403 when a referrer header is sent.
 */
function UserAvatar({
  displayName,
  photoURL,
  size,
  radius = CIRCLE_RADIUS,
  className,
  iconSize = 18,
}: Readonly<IUserAvatarProps>): JSX.Element {
  // Collapse null and empty names to undefined so the initials path only runs
  // for a real name. Mantine's placeholder prefers `children` over initials, so
  // the icon child is passed only when there is no name to draw initials from.
  const name = displayName || undefined;
  // Some Google Workspace accounts hand back a default-avatar URL (a generic
  // silhouette) through sign-in even when a real photo exists, so treat those as
  // no photo and let the initials show instead. See isDefaultGoogleAvatarUrl.
  const usablePhotoURL = photoURL && !isDefaultGoogleAvatarUrl(photoURL) ? photoURL : undefined;
  return (
    <Avatar
      src={usablePhotoURL}
      alt={name}
      name={name}
      color={name ? 'initials' : undefined}
      imageProps={{ referrerPolicy: 'no-referrer' }}
      size={size}
      radius={radius}
      className={className}
    >
      {name ? undefined : <IconUser size={iconSize} stroke={1.75} />}
    </Avatar>
  );
}

export default UserAvatar;
